import React, { useEffect, useMemo, useState } from 'react';
import { authToken } from '../authToken';
import { API_BASE_URL } from '../config';

function getSignedAmountStyle(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return undefined;
  // Requested mapping: positive -> red, negative -> green
  return {
    color: n > 0 ? '#c00' : '#0a0',
    fontWeight: 800,
  };
}

export default function BalanceAccountPanel({ onClose }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [menuAccountId, setMenuAccountId] = useState(null);
  const [confirmDeleteAccountId, setConfirmDeleteAccountId] = useState(null);

  // Add form state
  const [newTarget, setNewTarget] = useState('');
  const [newAmountForTarget, setNewAmountForTarget] = useState('');
  const [newComment, setNewComment] = useState('');
  const [addError, setAddError] = useState(null);

  const fetchAccounts = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/balanceaccounts`, {
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch balance accounts');
        return res.json();
      })
      .then(data => {
        setAccounts(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const sortedAccounts = useMemo(() => {
    return [...accounts].sort((a, b) => (a.target || '').localeCompare(b.target || ''));
  }, [accounts]);

  const totalAmountForTarget = useMemo(() => {
    return accounts.reduce((sum, a) => sum + (Number(a.amountForTarget) || 0), 0);
  }, [accounts]);

  const handleAdd = () => {
    setAddError(null);

    const target = (newTarget || '').trim();
    const amountNum = Number(newAmountForTarget);

    if (!target) {
      setAddError('Podaj target.');
      return;
    }
    if (!Number.isFinite(amountNum) || !Number.isInteger(amountNum)) {
      setAddError('Kwota musi być liczbą całkowitą.');
      return;
    }

    fetch(`${API_BASE_URL}/balanceaccounts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': authToken } : {})
      },
      body: JSON.stringify({
        target,
        amountForTarget: amountNum,
        comment: (newComment || '').trim()
      })
    })
      .then(res => {
        if (!res.ok) throw new Error('Nie udało się dodać konta');
        return res.json();
      })
      .then(() => {
        setShowAddPanel(false);
        setNewTarget('');
        setNewAmountForTarget('');
        setNewComment('');
        fetchAccounts();
      })
      .catch(err => setAddError(err.message));
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: '24px 12px 18px 12px',
      boxShadow: '0 2px 18px rgba(0,0,0,0.22)',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
      minWidth: 0,
      width: '700px',
      maxWidth: '98vw',
      minHeight: 0,
      height: '650px',
      maxHeight: '96vh',
      overflow: 'auto',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 16, fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }}
        aria-label="Close"
      >✖️</button>

      <h2 style={{ marginBottom: 16 }}>Salda, długi, rozliczenia</h2>

      <div style={{
        display: 'flex',
        gap: 24,
        marginBottom: 18,
        fontSize: '1.08rem',
        fontWeight: 500,
        color: '#333',
        justifyContent: 'space-between',
        background: '#eef8ff',
        borderRadius: 8,
        padding: '10px 18px',
        border: '1px solid #09f',
      }}>
        <span>Wpisów: <b>{accounts.length}</b></span>
        <span>
          Razem: <b style={getSignedAmountStyle(totalAmountForTarget)}>{totalAmountForTarget}</b>
        </span>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button
          style={{
            flex: 1,
            padding: '10px 0',
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 8,
            border: '1px solid #09f',
            background: '#eef8ff',
            color: '#036',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onClick={() => setShowAddPanel(true)}
        >➕ Dodaj</button>
      </div>

      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>Error: {error}</div>}

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
        <thead>
          <tr style={{ background: '#f8f8f8' }}>
            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600 }}>Kto?</th>
            <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600 }}>Kwota</th>
            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600 }}>Desc</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {sortedAccounts.map((acc) => (
            <tr key={acc.id ?? `${acc.target}-${acc.amountForTarget}-${acc.comment}`} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '8px' }}>{acc.target}</td>
              <td style={{ padding: '8px', textAlign: 'right' }}>
                <span style={getSignedAmountStyle(acc.amountForTarget)}>{acc.amountForTarget}</span>
              </td>
              <td style={{ padding: '8px', whiteSpace: 'pre-wrap' }}>{acc.comment}</td>
              <td style={{ padding: '8px', textAlign: 'center', position: 'relative' }}>
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 22,
                    cursor: 'pointer',
                    color: '#888',
                    marginLeft: 8
                  }}
                  aria-label="Menu"
                  onClick={e => {
                    e.stopPropagation();
                    setMenuAccountId(acc.id);
                  }}
                >⋯</button>

                {menuAccountId === acc.id && (
                  <div style={{
                    position: 'absolute',
                    background: '#fff',
                    border: '1px solid #eee',
                    borderRadius: 8,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                    padding: 12,
                    zIndex: 12000,
                    right: 24,
                    top: 40
                  }}>
                    <button
                      style={{ display: 'block', width: '100%', padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#a22' }}
                      onClick={() => { setMenuAccountId(null); setConfirmDeleteAccountId(acc.id); }}
                    >Delete</button>
                    <button
                      style={{ display: 'block', width: '100%', padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}
                      onClick={() => setMenuAccountId(null)}
                    >Cancel</button>
                  </div>
                )}

                {confirmDeleteAccountId === acc.id && (
                  <div style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%,-50%)',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
                    padding: 28,
                    zIndex: 13000,
                    minWidth: 240,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 16
                  }}>
                    <div style={{ fontSize: '1.1rem', marginBottom: 8 }}>Delete entry <b>{acc.target}</b>?</div>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <button
                        onClick={() => {
                          fetch(`${API_BASE_URL}/balanceaccounts/${acc.id}`, {
                            method: 'DELETE',
                            headers: {
                              ...(authToken ? { 'Authorization': authToken } : {})
                            }
                          })
                            .then(res => {
                              if (!res.ok) throw new Error('Failed to delete');
                              setConfirmDeleteAccountId(null);
                              fetchAccounts();
                            })
                            .catch(err => {
                              setError(err.message);
                              setConfirmDeleteAccountId(null);
                            });
                        }}
                        style={{
                          padding: '10px 24px',
                          fontSize: '1.05rem',
                          fontWeight: 600,
                          borderRadius: 8,
                          border: '1px solid #f66',
                          background: '#fff0f0',
                          color: '#a22',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                      >Delete</button>
                      <button
                        onClick={() => setConfirmDeleteAccountId(null)}
                        style={{
                          padding: '10px 24px',
                          fontSize: '1.05rem',
                          fontWeight: 600,
                          borderRadius: 8,
                          border: '1px solid #bbb',
                          background: '#f8f8f8',
                          color: '#555',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                      >Cancel</button>
                    </div>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!loading && accounts.length === 0 && <div>No balance accounts.</div>}

      {showAddPanel && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
          padding: 28,
          zIndex: 13000,
          minWidth: 320,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16
        }}>
          <div style={{ fontSize: '1.1rem', marginBottom: 8 }}>Dodaj wpis</div>

          <input
            type="text"
            placeholder="Kto?"
            value={newTarget}
            onChange={e => setNewTarget(e.target.value)}
            style={{ fontSize: '1.05rem', padding: '8px', borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
            autoFocus
          />

          <input
            type="number"
            placeholder="Kwota (dla niego/niej)"
            value={newAmountForTarget}
            onChange={e => setNewAmountForTarget(e.target.value)}
            style={{ fontSize: '1.05rem', padding: '8px', borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
          />

          <textarea
            placeholder="komentarz/opis"
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            style={{ fontSize: '1.05rem', padding: '8px', borderRadius: 6, border: '1px solid #ccc', width: '100%', minHeight: 90, resize: 'vertical' }}
          />

          {addError && <div style={{ color: 'red', fontSize: '0.98rem' }}>{addError}</div>}

          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button
              onClick={handleAdd}
              style={{ padding: '10px 24px', fontSize: '1.05rem', fontWeight: 600, borderRadius: 8, border: '1px solid #09f', background: '#eef8ff', color: '#036', cursor: 'pointer', transition: 'background 0.2s' }}
            >Add</button>
            <button
              onClick={() => {
                setShowAddPanel(false);
                setAddError(null);
                setNewTarget('');
                setNewAmountForTarget('');
                setNewComment('');
              }}
              style={{ padding: '10px 24px', fontSize: '1.05rem', fontWeight: 600, borderRadius: 8, border: '1px solid #bbb', background: '#f8f8f8', color: '#555', cursor: 'pointer', transition: 'background 0.2s' }}
            >Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
